import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CidaasService } from './../../../service/cidaas.service';
import { SharedDataService } from './../../../service/shared-data.service';
import { RegexUtil } from './../../../helpers/regex.helper';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { RegistrationService } from 'src/app/service/registration.service';
import { ILocation, LocationHelper } from '../../../helpers/location.helper';
import { take, takeUntil } from 'rxjs/operators';
import { KEY_DEFAULT_ERROR } from '../../../model/error.model';
import { addXSSValidationToNameFields, Field, FieldDataType, isUserNameField } from '../../../model/domain';

interface IRegisterResponse {
    email_verified?: boolean;
    password_reset_link?: string;
    q?: string;
    sub?: string;
    suggested_action?: string;
    suggested_redirect_uri?: string;
    action?: string;
    redirect_uri?: string;
    userStatus?: string;
    trackId?: string;
    rememberMe?: boolean;
    lat?: string;
    lon?: string;
}

enum RegisterActions {
    Register_Success = 'REGISTER_SUCCESS',
    Deduplication = 'DEDUPLICATION',
    Verification = 'VERIFICATION',
    Login = 'Login',
    Auto_Login = 'AUTO_LOGIN',
}

@Component({
    selector: 'app-register',
    templateUrl: './register.component.html',
    styleUrls: ['./register.component.scss'],
})
export class RegisterComponent implements OnInit, OnDestroy {
    fields: any[] = [];
    dynamicFields = ['password', 'password_echo'];
    dynamicFformFields: any[] = [];
    enableDynamicFields: boolean = true;
    formId;
    passwordFields: Field[] = [];
    authmethods = new BehaviorSubject<string[]>([]);
    isValidForm$ = new BehaviorSubject(true);
    allowedAuthMethods: any = [];
    selectedAuthMethod: string = '';
    queryParams = { userid_hint: '', userid_type: '', requestId: '', invite_id: '', ui_locales: '' };
    passwordPolicy: any;
    rememberMe: boolean = false;
    isSubmitBtnClicked = false;
    dynamicFormOptions = {
        btnEnabled: false,
    };
    dynamicForm: any;
    errorMessage: any;
    usernameField: any;
    buttonOptions: any = {
        register: {
            color: 'accent',
            text: 'REGISTER.REGISTER',
            alignment: 'right',
            type: 'proceed',
            underline: 'none',
            for: 'cidaasSubmitBtn',
            formId: 'formId',
        },
    };
    btnDisabled = false;
    showPasswordless: boolean = false;
    inviteId: string = '';
    inviteUserDetails: any;
    location: ILocation = {
        lat: '',
        lon: '',
    };
    destroy$ = new Subject();
    pageLoading: boolean = false;
    isAppUser: boolean = false;

    constructor(
        private _cidaasSDK: CidaasService,
        private _dataService: SharedDataService,
        private _route: ActivatedRoute,
        private _router: Router,
        private _regex: RegexUtil,
        private _translate: TranslateService,
        private registrationSrv: RegistrationService
    ) { }

    getLatLon(): void {
        const lat = localStorage.getItem('lat');
        const lon = localStorage.getItem('lon');
        if (lat && lon) {
            this.location.lat = lat;
            this.location.lon = lon;
        } else {
            LocationHelper.getLocationInfo()
                .then((info: ILocation) => {
                    this.location = info;
                })
                .catch((error) => {
                    this.location.lat = '';
                    this.location.lon = '';
                });
        }
    }

    changeEmail() {
        this._router.navigate(['login'], {
            queryParams: {
                requestId: this.queryParams['requestId'],
            },
        });
    }

    ngOnInit(): void {
        this._dataService._getTranslationLoadStatus().pipe(
            takeUntil(this.destroy$)
        ).subscribe((status) => {
            if (status) {

                const observer = new MutationObserver(() => {
                    const emailInput = document.querySelector<HTMLInputElement>('input[name="email"]');
                    if (emailInput && emailInput.parentElement) {
                        emailInput.disabled = true;
                        emailInput.parentElement.style.backgroundColor = "#eee";
                        observer.disconnect(); // Stoppt das Beobachten, wenn das Element gefunden wurde
                    }
                });

                observer.observe(document.body, { childList: true, subtree: true });

                // acid custom only get location if not app user
                this._dataService._getIsAppUser().subscribe(data => {
                    if (data !== true) {
                        this.getLatLon();
                    } else {
                        this.isAppUser = true;
                    }
                });

                this._getPasswordPolicy();
                this._initQueryParams();
                let options = {
                    acceptlanguage:'de',
                    requestId: this.queryParams.requestId,
                };
                if (this.inviteId) {
                    this.getInviteUserData();
                }
                this._getRegistrationFields(options);
                if (this.fields && this.fields.length > 0) {
                    this.fields = [...this._addvalidations(this.fields)];
                }
                this.authmethods.subscribe((data) => {
                    this.allowedAuthMethods = data;
                });
            }
        })

    }

    _triggerFormSubmit = () => {
        this.isSubmitBtnClicked = true;
        setTimeout(() => {
            this.isSubmitBtnClicked = false;
        });
    };
    _onSubmit = (e) => {

        let payload: any = {};
        payload = this._getFieldsValue('SYSTEM', this.fields, e);
        payload['customFields'] = this._getFieldsValue('CUSTOM', this.fields, e);
        payload['provider'] = 'SELF';
        payload['invite_id'] = this.queryParams.invite_id;
        let headers = { requestId: this.queryParams.requestId, acceptlanguage: 'de' };
        this.pageLoading = true;
        this.btnDisabled = true;
        this._cidaasSDK
            ._register(payload, headers)
            .then((response: any) => {
                if (response.error_code && response.error_code == 403) {
                    this._router.navigate(['/error'], {
                        queryParams: { error_hint: String(response.error).toLocaleUpperCase() },
                        queryParamsHandling: 'merge',
                    });
                    this.pageLoading = false;
                    this.btnDisabled = false;
                }
                if (response && response.success) {
                    if (response?.data?.action && response?.data?.redirect_uri) {
                        this.registrationSrv._handleRegisterSuccessRedirection(response?.data);
                    } else {
                        this._handleRedirection(response.data);
                    }
                } else {
                    if (response && response.error) {
                        this.pageLoading = false;
                        this.btnDisabled = false;
                        this.errorMessage = this.registrationSrv.getErrorMessage(response.error);
                    }
                }
            })
            .catch((error) => {
                console.error(error);
            });
        return false;
    };

    _initQueryParams = () => {
        this._route.queryParams.subscribe((data) => {
            this.queryParams.requestId = data.requestId;
            this.queryParams.userid_hint = data.userIdHint;
            this.queryParams.userid_type = data.type;
            this.queryParams.invite_id = data.invite_id;
            this.queryParams.ui_locales = data.ui_locales;
            this.inviteId = data.invite_id;
        });
    };

    _getRegistrationFields = (options: any) => {
        this._cidaasSDK
            ._getRegistrationSetup(options)
            .then((response: any) => {
                if (response && response.data) {
                    let data = response.data.sort(function (a, b) {
                        var keyA = a.order,
                            keyB = b.order;
                        if (keyA < keyB) return -1;
                        if (keyA > keyB) return 1;
                        return 0;
                    });
                    if (this.inviteUserDetails && this.inviteUserDetails.id && this.inviteId) {
                        data = this.registrationSrv.updateValue(data, this.inviteUserDetails);
                    }
                    this.fields = data;
                    this.fields = this._addvalidations(data);
                    this.isValidForm$.next(this._validateFormData(this.fields));
                    this.dynamicFformFields = this.fields.filter((field) => {
                        return this.dynamicFields.includes(field.fieldKey);
                    });
                    this._getAuthSetup(this.fields);
                } else {
                    this.errorMessage = KEY_DEFAULT_ERROR;
                }
            })
            .catch((error) => {
                console.error(error);
            });
    }

    _addvalidations = (fields: any) => {
        if (fields) {
            fields.forEach((field) => {
                if (field.dataType == 'EMAIL') {
                    if (this.queryParams.userid_type == 'email') {
                        field['readonly'] = true;
                        field['value'] = this.queryParams.userid_hint;
                        this.usernameField = field.fieldKey;
                    }
                    if (field.fieldDefinition) {
                        field.fieldDefinition['pattern'] = this._regex.email;
                    } else {
                        field['fieldDefinition'] = {};
                        field['fieldDefinition']['pattern'] = this._regex.email;
                    }
                    field.localeText['pattern'] = this._translate.instant('FORM.VALIDATION.EMAIL');
                }
                if (field.dataType == 'MOBILE') {
                    if (this.queryParams.userid_type == 'mobile') {
                        field['readonly'] = true;
                        field['value'] = this.queryParams.userid_hint;
                        this.usernameField = field.fieldKey;
                    }
                    if (field['localeText']) {
                        field['localeText']['invalidmobile'] = this._translate.instant('ENTER_VALID_MOBILE');
                    } else {
                        field['localeText'] = { invalidmobile: this._translate.instant('ENTER_VALID_MOBILE') };
                    }
                    if (field['fieldDefinition']) {
                        field['fieldDefinition']['invalidmobile'] = this._translate.instant('ENTER_VALID_MOBILE');
                    } else {
                        field['fieldDefinition'] = { invalidmobile: this._translate.instant('ENTER_VALID_MOBILE') };
                    }
                }
                if (isUserNameField(field, this.queryParams.userid_type)) {
                    field['readOnly'] = true;
                    field['value'] = this.queryParams.userid_hint;
                    this.usernameField = field.fieldKey;
                }
                if (field.dataType === 'PASSWORD' && !field.fieldDefinition.matchWith) {
                    if (this.passwordPolicy && !field.fieldDefinition.matchWith) {
                        this._addPasswordPolicy(field);
                    }
                    if (field.fieldDefinition.matchWith) {
                        field.fieldDefinition.maxLength = undefined;
                        field.fieldDefinition.minLength = undefined;
                    }
                }

                const xssFieldAdded = addXSSValidationToNameFields(field);
                if (xssFieldAdded) {
                    this._translate
                        .get(xssFieldAdded.translation.key, xssFieldAdded.translation.params)
                        .pipe(
                            take(1)
                        )
                        .subscribe((res) => {
                            field = xssFieldAdded.setLocale(res);
                        });
                }

            });
            return fields;
        }
    };

    _getPasswordPolicy = () => {
        this._dataService._getClientInfo().subscribe((data) => {
            if (data.password_policy) {
                this.passwordPolicy = data.password_policy;
            }
            if (data && data.is_remember_me_selected) {
                this.rememberMe = true;
            }
            if (data.passwordless_enabled) {
                this.showPasswordless = true;
            } else {
                this.showPasswordless = false;
            }
        });
    };

    _addPasswordPolicy = (field: any) => {
        let validations = ['lowerAndUpperCase', 'maximumLength', 'minimumLength', 'noOfDigits', 'noOfSpecialChars'];
        validations.forEach((key) => {
            if (validations.includes(key)) {
                if (key == 'maximumLength') {
                    field.fieldDefinition['maxLength'] = this.passwordPolicy['maximumLength'] || undefined;
                    let translationValue = (field.fieldDefinition['maxLength'] === 1) ? "FORM.VALIDATION.PASSWORD.POLICY.CHAR_MAX_ONE" : "FORM.VALIDATION.PASSWORD.POLICY.CHAR_MAX";
                    this._translate
                        .get(translationValue, { count: field.fieldDefinition['maxLength'] })
                        .subscribe((res) => {
                            field.localeText['maxLength'] = res;
                        });
                }
                if (key == 'minimumLength') {
                    field.fieldDefinition['minLength'] = this.passwordPolicy['minimumLength'] || undefined;
                    let translationValue = (field.fieldDefinition['minLength'] === 1) ? "FORM.VALIDATION.PASSWORD.POLICY.CHAR_MIN_ONE" : "FORM.VALIDATION.PASSWORD.POLICY.CHAR_MIN";
                    this._translate
                        .get(translationValue, { count: field.fieldDefinition['minLength'] })
                        .subscribe((res) => {
                            field.localeText['minLength'] = res;
                        });
                }
                if (key == 'noOfDigits') {
                    field.fieldDefinition['digitsLength'] = this.passwordPolicy['noOfDigits'] || undefined;
                    let translationValue = (field.fieldDefinition['digitsLength'] === 1) ? "FORM.VALIDATION.PASSWORD.POLICY.CHAR_DIGIT_ONE" : "FORM.VALIDATION.PASSWORD.POLICY.CHAR_DIGIT";
                    this._translate
                        .get(translationValue, { count: field.fieldDefinition['digitsLength'] })
                        .subscribe((res) => {
                            field.localeText['digitsLength'] = res;
                        });
                }
                if (key == 'noOfSpecialChars') {
                    field.fieldDefinition['specialLength'] =
                        this.passwordPolicy['noOfSpecialChars'] || undefined;
                    let translationValue = (field.fieldDefinition['specialLength'] === 1) ? "FORM.VALIDATION.PASSWORD.POLICY.CHAR_SPECIAL_ONE" : "FORM.VALIDATION.PASSWORD.POLICY.CHAR_SPECIAL";
                    this._translate
                        .get(translationValue, { count: field.fieldDefinition['specialLength'] })
                        .subscribe((res) => {
                            field.localeText['specialLength'] = res;
                        });
                }
                if (key == 'lowerAndUpperCase') {
                    field.fieldDefinition['lowerupperCase'] = this.passwordPolicy['lowerAndUpperCase'] || undefined;
                    this._translate.get('FORM.VALIDATION.PASSWORD.POLICY.CHAR_LOWER_UPPER').subscribe((res) => {
                        field.localeText['lowerupperCase'] = res;
                    });
                }
            }
        });
    };

    _getFieldsValue = (fieldType: string, fields: any, formValue: any, groupName?: string) => {
        let customFields: any = {};
        if (groupName) {
            fields.map((field) => {
                if (field['parent_group_id'] == groupName) {
                    let key = field['fieldKey'];
                    customFields[key] = formValue[key] || null;
                }
            });
        } else {
            fields.map((field) => {
                if (fieldType == 'SYSTEM') {
                    if (field['fieldType'] == fieldType && field['parent_group_id'] != 'DEFAULT') {
                        let groupName = field['parent_group_id'];
                        customFields[groupName] = this._getFieldsValue(fieldType, fields, formValue, groupName);
                    } else if (field['fieldType'] == fieldType) {
                        let key = field['fieldKey'];
                        customFields[key] = formValue[key] || null;
                    }
                } else {
                    // removing nested groups from customFields
                    if (field['fieldType'] == fieldType) {
                        let key = field['fieldKey'];
                        customFields[key] = formValue[key] || null;
                    }
                }
            });
        }
        return customFields;
    };

    _handleRedirection = (data: IRegisterResponse): void => {
        if (data) {
            if (data.suggested_action == 'DEDUPLICATION' && data.trackId) {
                this._router.navigate(['/account_deduplication'], {
                    queryParams: {
                        requestId: this.queryParams.requestId,
                        trackId: data.trackId,
                        provider: 'SELF',
                        selectedAuthMethod: this.selectedAuthMethod,
                    },
                });
            } else if (data.suggested_action === 'LOGIN' && data.trackId) {
                data.rememberMe = this.rememberMe;
                data.lat = this.location.lat;
                data.lon = this.location.lon;
                this._cidaasSDK._loginAfterRegister(data).catch(e => {
                    this.errorMessage = KEY_DEFAULT_ERROR;
                });

            } else {
                if (!data.email_verified) {
                    if (this.selectedAuthMethod && this.selectedAuthMethod !== 'password') {
                        this.callInitiateAccountVerification(data);
                    } else {
                        this.navigateToVerificationInit(data);
                    }
                } else {
                    this._router.navigate(['/register_success'], { queryParams: { requestid: this.queryParams.requestId } });
                }
            }
        }
    };

    callInitiateAccountVerification(data) {
        var initiateVerificationPayload = {
            processingType: 'CODE',
            requestId: this.queryParams.requestId,
            sub: data.sub,
            verificationMedium: this.selectedAuthMethod,
            lat: this.location.lat,
            lon: this.location.lon,
        };
        this.pageLoading = true;

        this._cidaasSDK.initiateAccountVerification(initiateVerificationPayload);
    }

    navigateToVerificationInit(data) {
        this._router.navigate(['/verification_init'], {
            queryParams: {
                requestid: this.queryParams.requestId,
                userStatus: data.userStatus,
                sub: data.sub,
                provider: 'SELF',
                type: this.selectedAuthMethod,
            },
        });
    }

    _getFormId = (e) => {
        this.formId = e;
    };

    _getAuthSetup = (fields: Field[]): void => {
        const allowedTypes = [FieldDataType.Password];
        if (this.showPasswordless) {
            allowedTypes.push(FieldDataType.Email, FieldDataType.Mobile);
        }
        const methods: string[] = [];
        fields.forEach((x: Field) => {
            if (allowedTypes.includes(x.dataType)) {
                switch (String(x.dataType)) {
                    case FieldDataType.Password:

                        this.passwordFields.push({ ...x });
                        // Mark the field as required to show password field
                        // as an authentication method
                        x.required = true;
                        if (!methods.includes('password')) {
                            methods.push('password');
                        }
                        break;
                    case FieldDataType.Email:
                        if (x.required && !methods.includes('email')) {
                            methods.push('email');
                        }
                        break;
                    case FieldDataType.Mobile:
                        if (x.required && !methods.includes('sms')) {
                            methods.push('ivr', 'sms');
                        }
                        break;
                }
                this.authmethods.next(methods);
            }
        });
    }

    _changeAuthSetup = (value) => {
        this.selectedAuthMethod = value;
        if (value === 'password') {
            this.enableDynamicFields = true;
            this.passwordFields.map((x) => {
                if (!this.fields.includes(x)) {
                    this.fields.push(x);
                }
            });
        } else {
            // [#788](https://gitlab.widas.de/cidaas-v2/ui/issues/-/issues/788)
            // we should not ignore mandatory fields during registration while switching the verification type
            this.enableDynamicFields = this.passwordFields.filter((x) => {
                return x.required;
            }).length > 0;
            if (!this.enableDynamicFields) {
                this.fields = this.fields.filter((x) => {
                    return x.dataType !== FieldDataType.Password;
                });
            }
        }
    }

    _back = () => {
        // this._router.navigate(['user',user.id], {relativeTo: this._activatedRoute})
        this._router.navigate(['login'], {
            queryParams: {
                requestId: this.queryParams.requestId,
                userIdHint: this.queryParams.userid_hint,
                method: this.usernameField,
            },
        });
    };
    _getDynamicFormGroup = (e) => {
        this.dynamicForm = e;
    };

    getInviteUserData() {
        this._cidaasSDK
            .getInviteUserDetails({ invite_id: this.inviteId })
            .then((response: any) => {
                if (response && response.data) {
                    this.inviteUserDetails = response.data;
                } else {
                    this.errorMessage = KEY_DEFAULT_ERROR;
                }
            })
            .catch((error) => {
                console.error(error);
            });
    }

    // checks whether all fields have proper translations.
    // if not it will return false
    _validateFormData = (data: any, returnFields: boolean = false) => {
        if (!returnFields) {
            return data.filter((x) => {
                return !x?.localeText;
            }).length > 0
                ? false
                : true;
        }
        return data
            .filter((x) => {
                return !x?.localeText;
            })
            .map((x) => {
                return x.fieldKey;
            });
    };

    /**
     *
     * @param data:IRegisterResponse
     *
     * The mothod will handle the register redirection according to the `action` and `redirect_uri`
     */
    _handleRegisterSuccessRedirection = (data: IRegisterResponse): void => {
        if (data?.redirect_uri && this._authenticateRedirectURI(data?.redirect_uri, data?.action)) {
            window.location.href = data?.redirect_uri;
        } else {
            console.warn('Cidaas: rediraction is not possible due to missing queryparams!');
        }
    };

    /**
     *
     * @param uri
     * @param action
     * @returns true/false
     *
     * The method will authenticate the given uri is valid or not
     */
    _authenticateRedirectURI = (uri: string, action: string = 'REGISTER_SUCCESS'): boolean => {
        switch (action) {
            case RegisterActions.Deduplication:
                return this._checkQueryParams(['trackId', 'requestId', 'provider'], uri);
                break;
            case RegisterActions.Verification:
                return this._checkQueryParams(['verification_type', 'requestId'], uri);
                break;
            case RegisterActions.Register_Success:
                return this._checkQueryParams(['requestId'], uri);
                break;
            default:
                return this._checkQueryParams(['requestId'], uri);
                break;
        }
        return true;
    };

    /**
     *
     * @param params
     * @param url
     * @returns
     */
    _checkQueryParams = (params: string[], url: string): boolean => {
        const searchparams = new URLSearchParams(url.substring(url.indexOf('?') + 1));
        return params.filter((x: string) => {
            return !searchparams.has(x);
        }).length
            ? false
            : true;
    };
    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.unsubscribe();
    }

    _onClickRegister(): void {
        console.log('test')
    }
}
